import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Phone,
  CheckCircle2,
  ArrowRight,
  Home,
  Sparkles,
  Building2,
  Users,
  RefreshCw,
  Truck,
} from "lucide-react";

const Services = () => {
  const services = [
    {
      id: "standard",
      icon: Home,
      title: "Standard House Cleaning",
      description: "Our regular cleaning service keeps your home fresh, tidy, and comfortable. Perfect for busy families and professionals who need consistent maintenance cleaning.",
      benefits: [
        "Maintain a consistently clean living space",
        "Reduce allergens and dust buildup",
        "More time for what matters most",
        "Affordable weekly or bi-weekly options",
      ],
      checklist: [
        "Dusting all surfaces and furniture",
        "Vacuuming carpets and rugs",
        "Mopping hard floors",
        "Kitchen surface cleaning",
        "Bathroom sanitization",
        "Trash removal",
        "Bed making",
        "General tidying",
      ],
      process: [
        "Initial assessment of your home",
        "Customized cleaning plan",
        "Scheduled cleaning visits",
        "Quality check after each service",
      ],
      price: "From $99",
    },
    {
      id: "deep",
      icon: Sparkles,
      title: "Deep Cleaning",
      description: "A thorough, top-to-bottom cleaning that tackles every corner of your home. Ideal for seasonal cleaning, before special events, or first-time clients.",
      benefits: [
        "Eliminates built-up grime and dirt",
        "Reaches often-neglected areas",
        "Improves indoor air quality",
        "Perfect reset for your home",
      ],
      checklist: [
        "Everything in standard cleaning, plus:",
        "Inside oven and refrigerator",
        "Cabinet fronts and interiors",
        "Baseboards and door frames",
        "Light fixtures and ceiling fans",
        "Window sills and blinds",
        "Behind and under furniture",
        "Detailed grout and tile cleaning",
      ],
      process: [
        "In-depth home walkthrough",
        "Focus area identification",
        "Comprehensive deep cleaning",
        "Final inspection with client",
      ],
      price: "From $199",
    },
    {
      id: "move",
      icon: Truck,
      title: "Move-In / Move-Out Cleaning",
      description: "Start fresh in your new home or leave your old one spotless. Our move cleaning ensures every surface is ready for the next chapter.",
      benefits: [
        "Get your full security deposit back",
        "Move into a sanitized space",
        "Reduce moving day stress",
        "Impress new tenants or buyers",
      ],
      checklist: [
        "Complete deep cleaning of all rooms",
        "Inside all cabinets and closets",
        "All appliances inside and out",
        "Light switch and outlet covers",
        "Window cleaning (interior)",
        "Garage floor sweeping",
        "Patio/balcony cleaning",
        "Final walkthrough",
      ],
      process: [
        "Schedule based on move date",
        "Empty home cleaning",
        "Thorough documentation",
        "Ready for inspection",
      ],
      price: "From $249",
    },
    {
      id: "apartment",
      icon: Building2,
      title: "Apartment Cleaning",
      description: "Specialized cleaning services designed for apartment living. We work efficiently in smaller spaces while delivering exceptional results.",
      benefits: [
        "Optimized for apartment layouts",
        "Quick turnaround times",
        "Flexible scheduling options",
        "Competitive pricing for renters",
      ],
      checklist: [
        "Full standard cleaning service",
        "Compact space optimization",
        "Balcony/patio tidying",
        "Common area consideration",
        "Pet-friendly options",
        "Quiet cleaning protocols",
        "Building rules compliance",
        "Key/access coordination",
      ],
      process: [
        "Building access arrangement",
        "Efficient cleaning execution",
        "Minimal disruption",
        "Secure lockup upon completion",
      ],
      price: "From $89",
    },
    {
      id: "office",
      icon: Building2,
      title: "Office Cleaning",
      description: "Professional commercial cleaning services for offices of all sizes. Create a productive, healthy workplace for your team.",
      benefits: [
        "Healthier work environment",
        "Improved employee productivity",
        "Professional appearance for clients",
        "Flexible after-hours scheduling",
      ],
      checklist: [
        "Desk and workstation cleaning",
        "Conference room maintenance",
        "Kitchen/break room sanitization",
        "Restroom deep cleaning",
        "Floor care (vacuum/mop)",
        "Trash and recycling removal",
        "Window and glass cleaning",
        "High-touch surface disinfection",
      ],
      process: [
        "Office walkthrough and assessment",
        "Custom cleaning schedule",
        "Trained commercial team",
        "Regular quality audits",
      ],
      price: "Custom Quote",
    },
    {
      id: "recurring",
      icon: RefreshCw,
      title: "Recurring Cleaning",
      description: "Set it and forget it! Our recurring cleaning plans keep your home consistently clean with scheduled weekly, bi-weekly, or monthly visits.",
      benefits: [
        "Consistent cleanliness year-round",
        "Discounted rates for regulars",
        "Same trusted cleaning team",
        "Priority scheduling",
      ],
      checklist: [
        "All standard cleaning tasks",
        "Customized focus areas",
        "Progressive deep cleaning rotation",
        "Seasonal updates included",
        "Supply management",
        "Flexible rescheduling",
        "Regular quality reviews",
        "Dedicated account manager",
      ],
      process: [
        "Initial deep cleaning",
        "Establish cleaning schedule",
        "Assign dedicated team",
        "Monthly check-ins",
      ],
      options: [
        { name: "Weekly", discount: "20% off" },
        { name: "Bi-Weekly", discount: "15% off" },
        { name: "Monthly", discount: "10% off" },
      ],
      price: "From $79/visit",
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Services</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mt-4">
              Professional Cleaning Services
            </h1>
            <p className="text-lg text-slate-600 mt-6">
              From routine maintenance to deep cleaning transformations, we have the perfect solution for your home or office.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-start ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-${service.id}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
                        {service.title}
                      </h2>
                      <p className="text-primary font-bold">{service.price}</p>
                    </div>
                  </div>

                  <p className="text-slate-600 text-lg mb-8">{service.description}</p>

                  {/* Benefits */}
                  <div className="mb-8">
                    <h3 className="font-heading font-bold text-secondary mb-4">Benefits</h3>
                    <ul className="space-y-3">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Recurring Options */}
                  {service.options && (
                    <div className="mb-8">
                      <h3 className="font-heading font-bold text-secondary mb-4">Subscription Options</h3>
                      <div className="flex flex-wrap gap-3">
                        {service.options.map((option, i) => (
                          <div key={i} className="bg-primary/10 px-4 py-2 rounded-full">
                            <span className="font-bold text-primary">{option.name}</span>
                            <span className="text-sm text-slate-600 ml-2">({option.discount})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex flex-wrap gap-4">
                    <Link to="/#quote-form">
                      <Button className="h-12 px-6 bg-accent hover:bg-accent/90 text-white font-bold rounded-full btn-accent-hover" data-testid={`service-${service.id}-quote-btn`}>
                        Get a Quote
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <a href="tel:+18717871311">
                      <Button variant="outline" className="h-12 px-6 border-2 border-slate-200 text-slate-700 font-bold rounded-full hover:border-primary hover:text-primary">
                        <Phone className="mr-2 w-4 h-4" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Cards */}
                <div className={`space-y-6 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  {/* Checklist Card */}
                  <Card className="bg-slate-50 border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-bold text-secondary mb-4">What's Included</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.checklist.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Process Card */}
                  <Card className="bg-white border-slate-100 rounded-2xl">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-bold text-secondary mb-4">Our Process</h3>
                      <ol className="space-y-3">
                        {service.process.map((step, i) => (
                          <li key={i} className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                              {i + 1}
                            </span>
                            <span className="text-slate-600">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Contact us for a free consultation. We'll help you find the perfect cleaning solution for your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#quote-form">
              <Button className="h-14 px-8 bg-accent hover:bg-accent/90 text-white font-bold rounded-full text-lg btn-accent-hover" data-testid="services-final-cta-btn">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18717871311">
              <Button variant="outline" className="h-14 px-8 border-2 border-white text-white font-bold rounded-full text-lg hover:bg-white hover:text-secondary">
                <Phone className="mr-2 w-5 h-5" />
                +1-871-7871311
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
